import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { UsersThree, Plus, ChatCircleDots, Lock, Globe, Users, Sparkle } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageIntro } from "@/components/PageIntro";

const TYPES = ["institution", "course", "class", "batch", "department", "reunion", "custom"];

export default function Groups() {
  const [tab, setTab] = useState("all");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "custom", description: "", privacy: "public" });

  const load = () => api.get("/groups", { params: { mine: tab === "mine" } }).then((r) => setGroups(r.data));
  useEffect(() => { load(); }, [tab]);

  const create = async () => {
    if (!form.name) return toast.error("Name is required");
    const { data } = await api.post("/groups", form);
    toast.success("Community created with its own chat room!");
    setOpen(false); setForm({ name: "", type: "custom", description: "", privacy: "public" });
    load();
  };

  const join = async (id, e) => { e.preventDefault(); await api.post(`/groups/${id}/join`); toast.success("Joined!"); load(); };

  return (
    <main className="mx-auto max-w-7xl px-5 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-secondary mb-1">A chat room for every group</p>
          <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Communities</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/sample-alumni"><Button variant="ghost" className="rounded-full gap-1.5 text-secondary hover:text-secondary" data-testid="see-sample-cta"><Sparkle size={16} weight="fill" /> See a sample profile</Button></Link>
            <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="create-group-btn"><Plus size={16} /> New community</Button></DialogTrigger>
          </div>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Create a community</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="GMEC CSE — Class of 2015" data-testid="group-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Privacy</Label>
                  <Select value={form.privacy} onValueChange={(v) => setForm({ ...form, privacy: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="public">Public</SelectItem><SelectItem value="private">Private</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <p className="text-xs text-muted-foreground">A dedicated chat room is auto-created with your community.</p>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full" data-testid="group-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <PageIntro
        title="Create an Alumni Group"
        tagline="Bring your classmates, friends, and fellow alumni together."
        intro={[
          "Create a dedicated alumni group for your class, batch, school, college, university, department, campus, graduating year, club, or special community and give everyone a place to reconnect, communicate, and stay involved.",
          "Whether you're bringing together a small group of classmates or building a community for thousands of alumni, AlumniPulse.net gives you the tools to create and manage your group in one place.",
        ]}
        sections={[
          { heading: "Build Your Community", body: "Give your group a name, description, institution, class or batch information, location, photos, and other details that help members recognize and join the right community." },
          { heading: "Connect & Communicate", body: "Every group can have its own persistent chat room, allowing members to share messages, photos, announcements, memories, and updates while staying connected over time." },
          { heading: "Organize Together", body: "Create and manage reunions, events, meetups, discussions, and community activities directly from your group. Keep members informed and track participation with RSVPs and guest lists." },
          { heading: "Grow Your Alumni Network", body: "Invite classmates and fellow alumni to join. As your community grows, members can discover old friends, reconnect with people they've lost touch with, and build new connections across generations." },
        ]}
        closing="Your class. Your memories. Your community."
        kicker="Create your alumni group and start reconnecting today."
      />
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList><TabsTrigger value="all" data-testid="tab-all">All communities</TabsTrigger><TabsTrigger value="mine" data-testid="tab-mine">My communities</TabsTrigger></TabsList>
      </Tabs>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {groups.length === 0 && <p className="text-muted-foreground">No communities yet. Create the first one!</p>}
        {groups.map((g, i) => (
          <Link key={g.id} to={`/groups/${g.id}`} data-testid="group-card">
            <Card className="group flex h-full flex-col border-border/60 p-6 shadow-sm transition-colors hover:border-secondary/40 animate-fade-up" style={{ animationDelay: `${i * 0.03}s` }}>
              <div className="flex items-start justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-secondary group-hover:text-secondary-foreground"><UsersThree size={22} weight="duotone" /></span>
                {g.privacy === "private" ? <Lock size={16} className="text-muted-foreground" /> : <Globe size={16} className="text-muted-foreground" />}
              </div>
              <h3 className="mt-4 font-heading text-lg font-medium leading-tight">{g.name}</h3>
              <Badge variant="secondary" className="mt-2 w-fit rounded-full font-normal capitalize">{g.type}</Badge>
              {g.description && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{g.description}</p>}
              <div className="mt-auto flex items-center justify-between pt-4">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Users size={14} /> {g.member_count} members</span>
                {g.is_member ? <span className="flex items-center gap-1 text-xs text-secondary"><ChatCircleDots size={14} weight="fill" /> Open chat</span>
                  : <Button size="sm" variant="outline" className="rounded-full h-7" onClick={(e) => join(g.id, e)} data-testid="join-group">Join</Button>}
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </main>
  );
}
